import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import { ref, set } from 'firebase/database'
import { app, db, auth, VAPID_KEY, firebaseConfig } from './firebase.js'

/**
 * iOS only delivers web push to a PWA installed on the home screen (Safari
 * 16.4+). In a normal Safari tab, Notification exists but permission can never
 * be granted — so we detect the standalone display mode and tell the user to
 * install rather than firing a request that silently does nothing.
 */
export function pushEnvironment() {
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const hasApi = 'Notification' in window && 'serviceWorker' in navigator

  if (!hasApi) return { ok: false, reason: 'unsupported', isIos, isStandalone }
  if (isIos && !isStandalone) return { ok: false, reason: 'needs-install', isIos, isStandalone }
  return { ok: true, reason: null, isIos, isStandalone }
}

/**
 * The service worker can't read import.meta.env, so the Firebase config rides
 * along as query params on the registration URL and the SW reads them back off
 * self.location. Keeps config out of a committed file while staying one plain
 * static asset.
 */
async function registerServiceWorker() {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey ?? '',
    projectId: firebaseConfig.projectId ?? '',
    messagingSenderId: firebaseConfig.messagingSenderId ?? '',
    appId: firebaseConfig.appId ?? '',
  })
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params}`, { scope: '/' })
}

/** Requests permission, registers the SW, and stores the token under this UID. */
export async function enablePush() {
  const env = pushEnvironment()
  if (!env.ok) return { ok: false, reason: env.reason }

  if (!(await isSupported())) return { ok: false, reason: 'unsupported' }
  if (!VAPID_KEY) return { ok: false, reason: 'no-vapid-key' }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return { ok: false, reason: 'denied' }

  const registration = await registerServiceWorker()
  const messaging = getMessaging(app)
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  })
  if (!token) return { ok: false, reason: 'no-token' }

  // Keyed by UID so re-enabling on the same device replaces rather than
  // accumulates. The crawler reads every value under /fcmTokens.
  await set(ref(db, `fcmTokens/${auth.currentUser.uid}`), {
    token,
    updatedAt: new Date().toISOString(),
    userAgent: navigator.userAgent.slice(0, 200),
  })

  return { ok: true, token }
}

/** Foreground messages don't raise a system notification; surface them in-app. */
export async function onForegroundMessage(handler) {
  if (!(await isSupported())) return () => {}
  return onMessage(getMessaging(app), handler)
}

export function notificationPermission() {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}
